import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { QueuePanel } from "@/components/QueuePanel";
import { Users, Swords, Calendar, Activity, Flame, Trophy, UserPlus, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="relative rounded-xl bg-[#0c0d14] border border-white/8 p-5 overflow-hidden">
    <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl`} style={{ background: `${color}22` }} />
    <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider mb-2">
      <Icon size={14} style={{ color }} /> {label}
    </div>
    <div className="font-display font-bold text-4xl">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/stats/dashboard").then(({ data }) => setD(data));
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  if (!d) return <div className="p-8 text-white/40">Đang tải...</div>;

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl">Dashboard</h1>
          <p className="text-white/40 text-sm mt-1">Tổng quan hệ thống đấu trường</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/matches/new" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black hover:opacity-90 flex items-center gap-2" data-testid="quick-new-match">
            <PlayCircle size={15} /> Tạo trận mới
          </Link>
          <Link to="/admin/players" className="px-4 py-2.5 rounded-md text-sm border border-white/10 hover:bg-white/5 flex items-center gap-2">
            <UserPlus size={15} /> Người chơi
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Tổng người chơi" value={d.total_players} color="#00e5ff" />
        <Stat icon={Calendar} label="Trận hôm nay" value={d.matches_today} color="#a855f7" />
        <Stat icon={Activity} label="Trận trong tuần" value={d.matches_week} color="#22ee9c" />
        <Stat icon={PlayCircle} label="Đang đấu" value={d.live_matches_count} color="#ff2a4d" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top 10 */}
        <div className="lg:col-span-2 rounded-xl bg-[#0c0d14] border border-white/8 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold flex items-center gap-2"><Trophy size={16} className="text-yellow-400" /> Top 10 ELO</h2>
            <Link to="/admin/leaderboard" className="text-xs text-cyan-300 hover:underline">Xem tất cả →</Link>
          </div>
          <div className="space-y-1.5">
            {d.top10.map((p, i) => (
              <div key={p.id} className="grid grid-cols-12 items-center gap-3 px-3 py-2 rounded-md hover:bg-white/5">
                <div className="col-span-1 font-display font-bold text-sm text-white/50">#{i + 1}</div>
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                  <span className="truncate text-sm">{p.nickname}</span>
                </div>
                <div className="col-span-3"><RankBadge elo={p.elo} size="sm" showElo={false} /></div>
                <div className="col-span-2 text-right font-display font-bold">{p.elo}</div>
                <div className="col-span-1 text-right text-red-400 text-xs flex items-center justify-end gap-0.5"><Flame size={10} />{p.win_streak}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {/* Queue */}
          <QueuePanel />

          {/* Live */}
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5">
            <h2 className="font-display font-semibold mb-4 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 pulse-ring" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
              </span>
              Đang diễn ra
            </h2>
            {d.live_matches.length === 0 ? (
              <div className="text-white/30 text-sm py-6 text-center">Không có</div>
            ) : (
              <div className="space-y-2">
                {d.live_matches.map((m) => (
                  <div key={m.id} className="p-3 rounded-md bg-white/[0.03] border border-cyan-400/20">
                    <div className="text-[10px] text-cyan-300 uppercase tracking-wider">Bàn {m.table_number} · BO{m.best_of}</div>
                    <div className="text-sm mt-1 truncate">{m.player_a_nickname} <span className="text-white/30 mx-1">vs</span> {m.player_b_nickname}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chuỗi thắng */}
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5">
            <h2 className="font-display font-semibold mb-4 flex items-center gap-2"><Flame size={16} className="text-red-400" /> Chuỗi thắng</h2>
            <div className="space-y-2">
              {d.top_streak.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <img src={p.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                  <span className="flex-1 text-sm truncate">{p.nickname}</span>
                  <span className="text-red-400 font-display font-bold">x{p.win_streak}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
